import clsx from "clsx";
import React from "react";
import {
  Box,
  Typography,
  CardMedia,
  Button,
  darken,
  Card,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

import { colorSystem } from "../../theme";
import { AppLogo } from "../app/AppLogo";
import { Avatar } from "../avatar/Avatar";

import {
  ProcessInviteCodeDto,
  ValidatedInviteCodeDto,
} from "@growth-machine-llc/stridist-api-client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useToastAlert } from "../app/ToastAlert/ToastAlertProvider";
import InviteCodesService from "../../services/InviteCodesService";
import { CLIENT_PROGRAMS_PROGRAM_ROUTE } from "../../routes/routes";
import {
  CLIENT_ENROLLMENTS_STATUS_COUNT_QUERY_KEY,
  CLIENT_PROGRAMS_LIST_QUERY_KEY,
} from "../../routes/client/programs/list/ClientProgramListRoute";

const useStyles = makeStyles((theme) => ({
  root: {
    borderRadius: theme.spacing(2),
    overflow: "hidden",
    boxShadow: theme.shadows[2],
  },

  cover: {
    width: "100%",
    backgroundColor: theme.palette.primary.light,
  },

  coverBody: {
    width: "100%",
    height: theme.spacing(30),
    padding: theme.spacing(4, 3),
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
    background: "linear-gradient(transparent, hsla(0, 0%, 0%, 0.6))",
  },

  footer: {
    padding: theme.spacing(3),
    backgroundColor: colorSystem.white2,
  },

  coach: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1.5),
  },

  coachName: {
    color: theme.palette.common.white,
    fontWeight: 700,
    fontSize: 14,
  },

  coachAvatar: {
    width: theme.spacing(4),
    height: theme.spacing(4),
    margin: theme.spacing(0, 1.5, 0, 0),
  },

  targetName: {
    color: theme.palette.common.white,
    fontWeight: 700,
    fontSize: 24,
    marginBottom: theme.spacing(2),
    lineHeight: 1.2,
  },

  joinButton: {
    fontWeight: 700,
    fontSize: 14,
    borderRadius: theme.spacing(3),
    padding: theme.spacing(1.25, 4),
    backgroundColor: theme.palette.common.white,
    boxShadow: theme.shadows[1],
    width: "fit-content",

    "&:hover": {
      backgroundColor: darken(theme.palette.common.white, 0.25),
    },
  },

  logo: {
    marginBottom: theme.spacing(2),
  },

  descriptionTitle: {
    fontSize: 14,
    fontWeight: 700,
    borderBottomWidth: 1,
    borderBottomStyle: "solid",
    borderBottomColor: theme.palette.quote,
    paddingBottom: theme.spacing(0.75),
    marginBottom: theme.spacing(2),
  },

  description: {
    fontSize: 14,
    fontWeight: 500,
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    WebkitLineClamp: 6,
    WebkitBoxOrient: "vertical",
  },

  shadowText: {
    textShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
  },
}));

export const INVALIDATE_KEYS_ON_ACCEPT_PROGRAM_INVITE_CODE = [
  CLIENT_ENROLLMENTS_STATUS_COUNT_QUERY_KEY,
  CLIENT_PROGRAMS_LIST_QUERY_KEY,
];

export interface ProgramInvitePreviewProps {
  invite: ValidatedInviteCodeDto;
  className?: string;
}

export function ProgramInvitePreview(props: ProgramInvitePreviewProps) {
  const { className, invite } = props;
  const s = useStyles();
  const { programInfo, coachInfo } = invite;

  const navigate = useNavigate();
  const { showToastAlert } = useToastAlert();

  const queryClient = useQueryClient();

  const { mutate: acceptInviteCode, isPending } = useMutation({
    mutationKey: ["accept-program-invite-code"],
    mutationFn: InviteCodesService.processInviteCode,
    onSuccess: (data: ProcessInviteCodeDto) => {
      if (data.programSlug == null) {
        showToastAlert("error", {
          message: "Failed to join program. Please try again later.",
        });
        return;
      }

      INVALIDATE_KEYS_ON_ACCEPT_PROGRAM_INVITE_CODE.forEach((queryKey) => {
        queryClient.invalidateQueries({
          queryKey: [queryKey],
          exact: false,
        });
      });

      navigate(
        CLIENT_PROGRAMS_PROGRAM_ROUTE.replace(":program", data.programSlug),
      );
    },
  });

  const handleConfirmInvite = React.useCallback(() => {
    acceptInviteCode({ code: invite.code });
  }, [acceptInviteCode, invite.code]);

  if (!programInfo) {
    return null;
  }

  return (
    <Card className={clsx(s.root, className)}>
      <CardMedia className={s.cover} image={programInfo.image}>
        <Box className={s.coverBody}>
          <Box className={s.coach}>
            <Avatar
              className={s.coachAvatar}
              displayName={coachInfo.displayName}
              photoURL={coachInfo.photoURL}
            />
            <Typography className={clsx(s.coachName, s.shadowText)}>
              {coachInfo.displayName}
            </Typography>
          </Box>
          <Typography className={clsx(s.targetName, s.shadowText)}>
            {programInfo.name}
          </Typography>
          <Button
            className={s.joinButton}
            onClick={handleConfirmInvite}
            disabled={isPending}
          >
            Get Started
          </Button>
        </Box>
      </CardMedia>

      <Box className={s.footer}>
        <AppLogo className={s.logo} />
        {programInfo.description && (
          <>
            <Typography className={s.descriptionTitle}>Description</Typography>
            <Typography className={s.description}>
              {programInfo.description}
            </Typography>
          </>
        )}
      </Box>
    </Card>
  );
}
